import React, { useState, useEffect } from "react";
import api from "../../../../api";
import {
 Card,
 Row,
 Col,
 Badge,
 ProgressBar,
 Table,
 Modal,
 Button
} from "react-bootstrap";
import {
 PieChart,
 Pie,
 Cell,
 Tooltip,
 Legend
} from "recharts";
import { useParams } from "react-router-dom";
import VerifiedCopyActions from "../../Common/VerifiedCopyActions";

const COLORS = ["#28A745", "#FFC107"]; // Approved, Pending

const MIRTab = () => {
 const { id: workOrderId } = useParams();
 const [mirStats, setMirStats] = useState({ total: 0, approved: 0, pending: 0 });
 const [mirList, setMirList] = useState([]);
 const [loading, setLoading] = useState(true);

 const [selectedMIR, setSelectedMIR] = useState(null);
 const [showModal, setShowModal] = useState(false);
 const [previewingPdf, setPreviewingPdf] = useState(false);

 useEffect(() => {
 fetchMIR();
 }, [workOrderId]);

 const fetchMIR = async () => {
 try {
 const res = await api.get("/mir", { params: { workOrder: workOrderId, limit: "all" } });

 if (res.data.success) {
 const list = res.data.data;

 // FIXED status field
 const total = list.length;
 const approved = list.filter(m => m.approvalStatus === "Approved").length;
 const pending = list.filter(m => m.approvalStatus === "Pending").length;

 setMirStats({ total, approved, pending });
 setMirList(list);
 }
 } catch (err) {
 console.error("Failed to fetch MIR:", err);
 } finally {
 setLoading(false);
 }
 };

 const approvalPercentage =
 mirStats.total === 0 ? 0 : (mirStats.approved / mirStats.total) * 100;

 const pieData = [
 { name: "Approved", value: mirStats.approved },
 { name: "Pending", value: mirStats.pending }
 ];

 // ------------------- PDF PREVIEW -------------------
 const handlePreviewPDF = async (id) => {
 try {
 setPreviewingPdf(true);
 const res = await api.get(`/mir/${id}/pdf`, { responseType: "blob" });
 const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
 window.open(url, "_blank");
 } catch (err) {
 alert("Failed to preview PDF");
 } finally {
 setPreviewingPdf(false);
 }
 };

 // ------------------- PDF DOWNLOAD -------------------
 const handleDownloadPDF = async (id) => {
 try {
 const res = await api.get(`/mir/${id}/pdf`, { responseType: "blob" });
 const url = window.URL.createObjectURL(new Blob([res.data]));
 const a = document.createElement("a");
 a.href = url;
 a.download = `MIR-${id}.pdf`;
 a.click();
 } catch (err) {
 alert("Failed to download PDF");
 }
 };

 // ------------------- VIEW DETAILS -------------------
 const handleView = async (id) => {
 try {
 const res = await api.get(`/mir/${id}`);
 if (res.data.success) {
 setSelectedMIR(res.data.data);
 setShowModal(true);
 }
 } catch (err) {
 alert("Failed to load MIR details");
 }
 };

 const formatDate = (date) =>
 date
 ? new Date(date).toLocaleDateString("en-IN", {
 year: "numeric",
 month: "short",
 day: "numeric",
 })
 : "—";

 if (loading) return <p>Loading MIR data...</p>;

 return (
 <>
 <Row className="g-4">
 {/* SUMMARY */}
 <Col md={4}>
 <Card className="shadow-sm border-0">
 <Card.Body>
 <h5 className="fw-bold mb-3">MIR Summary</h5>

 <p>Total MIR: <strong>{mirStats.total}</strong></p>
 <p className="text-success">Approved: <strong>{mirStats.approved}</strong></p>
 <p className="text-warning">Pending: <strong>{mirStats.pending}</strong></p>

 <ProgressBar
 now={approvalPercentage}
 label={`${approvalPercentage.toFixed(0)}% Approved`}
 />
 </Card.Body>
 </Card>
 </Col>

 {/* PIE CHART */}
 <Col md={4}>
 <Card className="shadow-sm border-0">
 <Card.Body>
 <h5 className="fw-bold mb-3">Status Distribution</h5>
 <PieChart width={250} height={250}>
 <Pie
 data={pieData}
 dataKey="value"
 nameKey="name"
 cx="50%"
 cy="50%"
 outerRadius={80}
 label
 >
 {pieData.map((entry, i) => (
 <Cell key={i} fill={COLORS[i]} />
 ))}
 </Pie>
 <Tooltip />
 <Legend />
 </PieChart>
 </Card.Body>
 </Card>
 </Col>

 {/* LIST TABLE */}
 <Col md={12}>
 <Card className="shadow-sm border-0">
 <Card.Body>
 <h5 className="fw-bold mb-3">MIR List</h5>
 <Table hover responsive>
 <thead>
 <tr>
 <th>#</th>
 <th>MIR No</th>
 <th>Status</th>
 <th>Date</th>
 <th>PDF</th>
 <th>View</th>
 </tr>
 </thead>
 <tbody>
 {mirList.map((m, i) => (
 <tr key={m._id}>
 <td>{i + 1}</td>
 <td><strong>{m.mirNumber}</strong></td>
 <td>
 <Badge className="rounded-pill" bg={m.approvalStatus === "Approved" ? "success" : "warning"}>
 {m.approvalStatus}
 </Badge>
 </td>
 <td>{formatDate(m.createdAt)}</td>
 <td>
 <VerifiedCopyActions 
 id={m._id} 
 type="mir" 
 hasVerifiedCopy={!!m.verifiedCopy?.filePath} 
 onUploadSuccess={fetchMIR}
 />
 </td>
 <td>
 <button className="btn btn-sm btn-outline-primary border-0" onClick={() => handleView(m._id)}>
 <i className="fas fa-eye"></i>
 </button>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* MIR DETAILS MODAL */}
 <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
 <Modal.Header closeButton>
 <Modal.Title>MIR Details</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 {selectedMIR && (
 <>
 <Row className="mb-3">
 <Col md={6}><strong>MIR Number:</strong> {selectedMIR.mirNumber}</Col>
 <Col md={6}><strong>Status:</strong> <Badge className="rounded-pill ms-2" bg={selectedMIR.approvalStatus === "Approved" ? "success" : "warning"}>{selectedMIR.approvalStatus}</Badge></Col>
 </Row>

 <Row className="mb-3">
 <Col md={6}><strong>Delivery:</strong> {selectedMIR.deliveryReference?.deliveryNumber || "—"}</Col>
 <Col md={6}><strong>Project:</strong> {selectedMIR.project?.projectName || selectedMIR.project?.project || "—"}</Col>
 </Row>

 <hr />
 <strong>Items:</strong>
 <ul>
 {selectedMIR.items?.map((it, i) => (
 <li key={i}>
 {it.wlino && <span className="text-primary me-2">[WLI-{it.wlino}]</span>}
 {it.materialName || it.itemName} — {it.quantityInspected || it.quantity} {it.unit}
 </li>
 ))}
 </ul>

 {selectedMIR.remarks && (
 <>
 <hr />
 <strong>Remarks:</strong>
 <p className="text-muted">{selectedMIR.remarks}</p>
 </>
 )}
 </>
 )}
 </Modal.Body>
 <Modal.Footer>
 {selectedMIR && (
 <Button variant="primary" onClick={() => handlePreviewPDF(selectedMIR._id)}>
 Preview PDF
 </Button>
 )}
 <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
 </Modal.Footer>
 </Modal>

 {previewingPdf && (
 <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
 style={{ background: "rgba(0,0,0,0.4)", zIndex: 9999 }}>
 <div className="bg-white p-4 rounded shadow">Preparing PDF…</div>
 </div>
 )}
 </>
 );
};

export default MIRTab;
